import * as React from "react";
import { useState, useEffect, useCallback } from "react";

import axios from "axios";

import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Switch,
  Box,
  InputAdornment,
  Link,
  Checkbox,
  ListItemText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterListIcon from "@material-ui/icons/FilterList";
import DescriptionIcon from "@material-ui/icons/Description";
import LayersIcon from "@material-ui/icons/Layers";
import InfoIcon from "@material-ui/icons/Info";
import EditIcon from "@material-ui/icons/Edit";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost, getConfigPatch, getConfigDelete, getConfigPut } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "stretch",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    padding: "2em",
  },
  mainHeading: {
    textAlign: "center",
    marginBottom: "1em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  metadataContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "1.5em",
    marginBottom: "2em",
    flexWrap: "wrap",
  },
  metadataCard: {
    minWidth: 200,
    textAlign: "center",
  },
  metadataValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  metadataLabel: {
    color: theme.palette.text.secondary,
  },
  filtersContainer: {
    marginBottom: "1.5em",
    padding: "1.5em",
  },
  searchField: {
    minWidth: 300,
  },
  filterSelect: {
    minWidth: 150,
  },
  tableContainer: {
    marginTop: "1em",
    width: "100%",
    overflowX: "auto",
  },
  table: {
    minWidth: 1200,
  },
  tableCell: {
    padding: "12px 16px",
    border: "1px solid #e0e0e0",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  merchantLogo: {
    width: 40,
    height: 40,
    objectFit: "contain",
    borderRadius: 4,
  },
  chipBlacklisted: {
    backgroundColor: "#f44336",
    color: "white",
  },
  chipEnabled: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  chipDisabled: {
    backgroundColor: "#9e9e9e",
    color: "white",
  },
  chipSpecialOffer: {
    backgroundColor: "#ff9800",
    color: "white",
    marginRight: 4,
  },
  chipSpeedyRewarding: {
    backgroundColor: "#2196f3",
    color: "white",
    marginRight: 4,
  },
  chipTiers: {
    backgroundColor: "#9c27b0",
    color: "white",
  },
  actionButton: {
    marginRight: 4,
  },
  noDataMessage: {
    textAlign: "center",
    color: theme.palette.grey[500],
    fontStyle: "italic",
    margin: "2em 0",
  },
  dialogContent: {
    minWidth: 400,
  },
  trackingUrlContainer: {
    marginTop: "1em",
    padding: "1em",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    wordBreak: "break-all",
  },
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "4em",
  },
  rateCell: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
}));

interface VdxTier {
  id: number;
  name: string;
  rate: string;
  rate_was?: string;
  rate_fixed: string;
  rate_numeric: string;
  rate_numeric_was?: string;
  actual_from?: string;
  actual_to?: string;
}

interface PurchaseTimelineStats {
  awarded: {
    time: number;
    unit: string;
  };
  tracked: {
    time: number;
    unit: string;
  };
}

interface RewardPaidOverride {
  time?: number;
  qualifier?: string;
}

const REWARD_PAID_QUALIFIERS = [
  { value: "after purchase", label: "After Purchase" },
  { value: "after you travel", label: "After Travel" },
  { value: "after your stay", label: "After Stay" },
  { value: "after policy starts", label: "After Policy Starts" },
  { value: "after delivery", label: "After Delivery" },
  { value: "after subscription starts", label: "After Subscription Starts" },
];

const BLACKLIST_REASON_CODES = [
  { value: "broken_url", label: "Broken URL (auto re-enable on pass)" },
  { value: "duplicate", label: "Duplicate (never auto re-enable)" },
  { value: "bad_redirect", label: "Bad Redirect" },
  { value: "manual", label: "Manual" },
  { value: "unknown", label: "Unknown" },
] as const;

const formatBlacklistReasonCode = (code: string | null | undefined): string => {
  if (!code) return "";
  const match = BLACKLIST_REASON_CODES.find((c) => c.value === code);
  return match?.label || code;
};

interface VdxMerchant {
  id: number;
  merchant_id: string;
  merchant_name: string;
  logo: string | null;
  category: string | null;
  tiers: VdxTier[] | null;
  description: string | null;
  rate: string | null;
  rate_numeric: number | null;
  purchase_timeline_stats: PurchaseTimelineStats | null;
  reward_paid_override: RewardPaidOverride | null;
  status: string;
  is_blacklisted: boolean;
  blacklist_reason: string | null;
  blacklist_reason_code: string | null;
  blacklist_watch: boolean;
  is_enabled_on_app: boolean;
  special_offer: boolean;
  speedy_rewarding: boolean;
  flagged: boolean;
}

interface EditOverrideForm {
  merchant_name_override: string;
  merchant_logo_override: string;
  reward_paid_time: string;
  reward_paid_qualifier: string;
  blacklist_reason: string;
  blacklist_reason_code: string;
  blacklist_watch: boolean;
}

interface BlacklistForm {
  reason: string;
  reason_code: string;
  watch: boolean;
}

interface Pagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface CategoryBreakdown {
  category: string;
  total_brands: number;
  enabled_on_app_count: number;
  blacklisted_count: number;
}

interface Metadata {
  duration_ms: number;
  total_brands: number;
  enabled_on_app_count: number;
  blacklisted_count: number;
  categories?: CategoryBreakdown[];
}

interface TestBrandResult {
  merchant_id: string;
  merchant_name: string;
  click_id: string;
  tracking_url: string;
  tracking_mobile_url: string;
  test_user_email: string;
}

interface VdxBrandListResponse {
  success: boolean;
  status?: boolean;
  message: string;
  data: VdxMerchant[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
  };
  metadata: Metadata;
}

interface VdxBlacklistResponse {
  status: boolean;
  success?: boolean;
  message: string;
  data: {
    merchant_id: string;
    merchant_name: string;
  };
}

interface VdxBrandTestResponse {
  status: boolean;
  success?: boolean;
  message: string;
  data: TestBrandResult;
}

const VdxMerchantsDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  // Data state
  const [merchants, setMerchants] = useState<VdxMerchant[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    pageSize: 25,
    totalPages: 0,
  });
  const [metadata, setMetadata] = useState<Metadata>({
    duration_ms: 0,
    total_brands: 0,
    enabled_on_app_count: 0,
    blacklisted_count: 0,
    categories: [],
  });

  // Filter state
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [isBlacklistedFilter, setIsBlacklistedFilter] = useState<string>("");
  const [isEnabledOnAppFilter, setIsEnabledOnAppFilter] = useState<string>("");
  const [specialOfferFilter, setSpecialOfferFilter] = useState<string>("");
  const [speedyRewardingFilter, setSpeedyRewardingFilter] = useState<string>("");
  const [hasTiersFilter, setHasTiersFilter] = useState<string>("");
  const [showInactive, setShowInactive] = useState(false);
  const [sortBy, setSortBy] = useState("merchant_name");
  const [sortOrder, setSortOrder] = useState("ASC");

  // Loading state
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Dialog state
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [unblacklistDialogOpen, setUnblacklistDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [tiersDialogOpen, setTiersDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<VdxMerchant | null>(null);
  const [testResult, setTestResult] = useState<TestBrandResult | null>(null);
  const [blacklistForm, setBlacklistForm] = useState<BlacklistForm>({
    reason: "",
    reason_code: "manual",
    watch: false,
  });
  const [editForm, setEditForm] = useState<EditOverrideForm>({
    merchant_name_override: "",
    merchant_logo_override: "",
    reward_paid_time: "",
    reward_paid_qualifier: "",
    blacklist_reason: "",
    blacklist_reason_code: "",
    blacklist_watch: false,
  });

  // Fetch merchants
  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy,
        sortOrder,
      };

      if (search) params.search = search;
      if (categoryFilter.length > 0) params.category = categoryFilter.join(",");
      if (isBlacklistedFilter) params.is_blacklisted = isBlacklistedFilter;
      if (isEnabledOnAppFilter) params.is_enabled_on_app = isEnabledOnAppFilter;
      if (specialOfferFilter) params.special_offer = specialOfferFilter;
      if (speedyRewardingFilter) params.speedy_rewarding = speedyRewardingFilter;
      if (hasTiersFilter) params.has_tiers = hasTiersFilter;
      if (showInactive) params.show_inactive = "true";

      const config = getConfigGet(params);
      const response = await axios.get<VdxBrandListResponse>(ENDPOINTS.VDX_BRANDS, config);

      if (response.data && (response.data.success || response.data.status)) {
        setMerchants(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          totalPages: response.data.pagination?.total_pages || 0,
        }));
        setMetadata(response.data.metadata || {
          duration_ms: 0,
          total_brands: 0,
          enabled_on_app_count: 0,
          blacklisted_count: 0,
          categories: [],
        });
      } else {
        notify(response.data?.message || "Failed to fetch merchants", "error");
      }
    } catch (error: any) {
      console.error("Error fetching VDX merchants:", error);
      notify(
        error?.response?.data?.message || "Failed to fetch VDX merchants",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.pageSize,
    search,
    categoryFilter,
    isBlacklistedFilter,
    isEnabledOnAppFilter,
    specialOfferFilter,
    speedyRewardingFilter,
    hasTiersFilter,
    showInactive,
    sortBy,
    sortOrder,
    notify,
  ]);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  // Handle search
  const handleSearch = () => {
    setSearch(searchInput);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 1,
    }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchInput("");
    setSearch("");
    setCategoryFilter([]);
    setIsBlacklistedFilter("");
    setIsEnabledOnAppFilter("");
    setSpecialOfferFilter("");
    setSpeedyRewardingFilter("");
    setHasTiersFilter("");
    setShowInactive(false);
    setSortBy("merchant_name");
    setSortOrder("ASC");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Blacklist merchant
  const handleBlacklist = async () => {
    if (!selectedMerchant) return;

    const reason = blacklistForm.reason.trim();
    if (!reason) {
      notify("A blacklist reason is required", "error");
      return;
    }

    setActionLoading(selectedMerchant.merchant_id);
    try {
      const headers = getConfigPost();
      const payload: Record<string, unknown> = {
        merchant_id: selectedMerchant.merchant_id,
        merchant_name: selectedMerchant.merchant_name,
        reason,
        watch: blacklistForm.watch,
      };
      if (blacklistForm.reason_code) {
        payload.reason_code = blacklistForm.reason_code;
      }

      await axios.post<VdxBlacklistResponse>(
        ENDPOINTS.VDX_BRANDS,
        payload,
        { headers }
      );
      notify(`${selectedMerchant.merchant_name} added to blacklist`, "info");
      setBlacklistDialogOpen(false);
      setSelectedMerchant(null);
      setBlacklistForm({ reason: "", reason_code: "manual", watch: false });
      fetchMerchants();
    } catch (error: any) {
      console.error("Error blacklisting merchant:", error);
      notify(
        error?.response?.data?.message || "Failed to blacklist merchant",
        "error"
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Unblacklist merchant
  const handleUnblacklist = async () => {
    if (!selectedMerchant) return;

    setActionLoading(selectedMerchant.merchant_id);
    try {
      const headers = getConfigDelete();
      await axios.delete<VdxBlacklistResponse>(
        `${ENDPOINTS.VDX_BRANDS}/${selectedMerchant.merchant_id}`,
        { headers }
      );
      notify(`${selectedMerchant.merchant_name} removed from blacklist`, "info");
      setUnblacklistDialogOpen(false);
      setSelectedMerchant(null);
      fetchMerchants();
    } catch (error: any) {
      console.error("Error removing merchant from blacklist:", error);
      notify(
        error?.response?.data?.message || "Failed to remove merchant from blacklist",
        "error"
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Test brand
  const handleTestBrand = async () => {
    if (!selectedMerchant) return;

    setActionLoading(selectedMerchant.merchant_id);
    setTestResult(null);
    try {
      const headers = getConfigPatch();
      const response = await axios.patch<VdxBrandTestResponse>(
        `${ENDPOINTS.VDX_BRANDS}/${selectedMerchant.merchant_id}`,
        {},
        { headers }
      );
      if (response.data && (response.data.status || response.data.success)) {
        setTestResult(response.data.data);
        notify("Tracking URL generated successfully", "info");
      } else {
        notify(response.data?.message || "Failed to test brand", "error");
      }
    } catch (error: any) {
      console.error("Error testing brand:", error);
      notify(
        error?.response?.data?.message || "Failed to test brand",
        "error"
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Open dialogs
  const openBlacklistDialog = (merchant: VdxMerchant) => {
    setSelectedMerchant(merchant);
    setBlacklistForm({ reason: "", reason_code: "manual", watch: false });
    setBlacklistDialogOpen(true);
  };

  const openUnblacklistDialog = (merchant: VdxMerchant) => {
    setSelectedMerchant(merchant);
    setUnblacklistDialogOpen(true);
  };

  const openTestDialog = (merchant: VdxMerchant) => {
    setSelectedMerchant(merchant);
    setTestResult(null);
    setTestDialogOpen(true);
  };

  const openDescriptionDialog = (merchant: VdxMerchant) => {
    setSelectedMerchant(merchant);
    setDescriptionDialogOpen(true);
  };

  const openTiersDialog = (merchant: VdxMerchant) => {
    setSelectedMerchant(merchant);
    setTiersDialogOpen(true);
  };

  const openDetailsDialog = (merchant: VdxMerchant) => {
    setSelectedMerchant(merchant);
    setDetailsDialogOpen(true);
  };

  const openEditDialog = (merchant: VdxMerchant) => {
    setSelectedMerchant(merchant);
    setEditForm({
      merchant_name_override: "",
      merchant_logo_override: "",
      reward_paid_time: merchant.reward_paid_override?.time?.toString() || "",
      reward_paid_qualifier: merchant.reward_paid_override?.qualifier || "",
      blacklist_reason: merchant.blacklist_reason || "",
      blacklist_reason_code: merchant.blacklist_reason_code || "",
      blacklist_watch: Boolean(merchant.blacklist_watch),
    });
    setEditDialogOpen(true);
  };

  const handleUpdateOverrides = async () => {
    if (!selectedMerchant) return;

    setActionLoading(selectedMerchant.merchant_id);
    try {
      const headers = getConfigPut();
      
      const payload: any = {};
      
      if (editForm.merchant_name_override.trim()) {
        payload.merchant_name_override = editForm.merchant_name_override.trim();
      }
      
      if (editForm.merchant_logo_override.trim()) {
        payload.merchant_logo_override = editForm.merchant_logo_override.trim();
      }
      
      if (editForm.reward_paid_time || editForm.reward_paid_qualifier) {
        payload.reward_paid_override = {};
        if (editForm.reward_paid_time) {
          payload.reward_paid_override.time = parseInt(editForm.reward_paid_time, 10);
        }
        if (editForm.reward_paid_qualifier) {
          payload.reward_paid_override.qualifier = editForm.reward_paid_qualifier;
        }
      }

      if (selectedMerchant.is_blacklisted) {
        const reason = editForm.blacklist_reason.trim();
        if (!reason) {
          notify("Blacklist reason cannot be empty", "error");
          setActionLoading(null);
          return;
        }
        payload.blacklist_reason = reason;
        payload.blacklist_reason_code = editForm.blacklist_reason_code || null;
        payload.blacklist_watch = editForm.blacklist_watch;
      }

      await axios.put(
        `${ENDPOINTS.VDX_BRANDS}/${selectedMerchant.merchant_id}`,
        payload,
        { headers }
      );
      
      notify(`Overrides updated for ${selectedMerchant.merchant_name}`, "info");
      setEditDialogOpen(false);
      setSelectedMerchant(null);
      setEditForm({
        merchant_name_override: "",
        merchant_logo_override: "",
        reward_paid_time: "",
        reward_paid_qualifier: "",
        blacklist_reason: "",
        blacklist_reason_code: "",
        blacklist_watch: false,
      });
      fetchMerchants();
    } catch (error: any) {
      console.error("Error updating overrides:", error);
      notify(
        error?.response?.data?.message || "Failed to update overrides",
        "error"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearOverrides = async () => {
    if (!selectedMerchant) return;

    setActionLoading(selectedMerchant.merchant_id);
    try {
      const headers = getConfigPut();
      
      const payload = {
        merchant_name_override: null,
        merchant_logo_override: null,
        reward_paid_override: null,
      };

      await axios.put(
        `${ENDPOINTS.VDX_BRANDS}/${selectedMerchant.merchant_id}`,
        payload,
        { headers }
      );
      
      notify(`Overrides cleared for ${selectedMerchant.merchant_name}`, "info");
      setEditDialogOpen(false);
      setSelectedMerchant(null);
      setEditForm({
        merchant_name_override: "",
        merchant_logo_override: "",
        reward_paid_time: "",
        reward_paid_qualifier: "",
        blacklist_reason: "",
        blacklist_reason_code: "",
        blacklist_watch: false,
      });
      fetchMerchants();
    } catch (error: any) {
      console.error("Error clearing overrides:", error);
      notify(
        error?.response?.data?.message || "Failed to clear overrides",
        "error"
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Render metadata cards
  const renderMetadataCards = () => (
    <div className={classes.metadataContainer}>
      <Card className={classes.metadataCard}>
        <CardContent>
          <Typography className={classes.metadataValue}>
            {metadata.total_brands.toLocaleString()}
          </Typography>
          <Typography className={classes.metadataLabel}>Total Brands</Typography>
        </CardContent>
      </Card>
      <Card className={classes.metadataCard}>
        <CardContent>
          <Typography className={classes.metadataValue} style={{ color: "#4caf50" }}>
            {metadata.enabled_on_app_count.toLocaleString()}
          </Typography>
          <Typography className={classes.metadataLabel}>Enabled on App</Typography>
        </CardContent>
      </Card>
      <Card className={classes.metadataCard}>
        <CardContent>
          <Typography className={classes.metadataValue} style={{ color: "#f44336" }}>
            {metadata.blacklisted_count.toLocaleString()}
          </Typography>
          <Typography className={classes.metadataLabel}>Blacklisted</Typography>
        </CardContent>
      </Card>
    </div>
  );

  // Render filters
  const renderFilters = () => (
    <Paper className={classes.filtersContainer}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            className={classes.searchField}
            label="Search by name or ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              multiple
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value as string[]);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              label="Category"
              renderValue={(selected) => {
                const selectedArray = selected as string[];
                if (selectedArray.length === 0) return "All Categories";
                if (selectedArray.length === 1) return selectedArray[0];
                return `${selectedArray.length} categories`;
              }}
            >
              {(metadata.categories || []).map((cat) => (
                <MenuItem key={cat.category} value={cat.category}>
                  <Checkbox checked={categoryFilter.indexOf(cat.category) > -1} color="primary" />
                  <ListItemText primary={cat.category} />
                  <Chip 
                    label={cat.total_brands} 
                    size="small" 
                    style={{ marginLeft: 8, height: 20, fontSize: '0.7rem' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Blacklisted</InputLabel>
            <Select
              value={isBlacklistedFilter}
              onChange={(e) => {
                setIsBlacklistedFilter(e.target.value as string);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              label="Blacklisted"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Blacklisted</MenuItem>
              <MenuItem value="false">Not Blacklisted</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Enabled on App</InputLabel>
            <Select
              value={isEnabledOnAppFilter}
              onChange={(e) => {
                setIsEnabledOnAppFilter(e.target.value as string);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              label="Enabled on App"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Enabled</MenuItem>
              <MenuItem value="false">Not Enabled</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Special Offer</InputLabel>
            <Select
              value={specialOfferFilter}
              onChange={(e) => {
                setSpecialOfferFilter(e.target.value as string);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              label="Special Offer"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Has Special Offer</MenuItem>
              <MenuItem value="false">No Special Offer</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Speedy Rewarding</InputLabel>
            <Select
              value={speedyRewardingFilter}
              onChange={(e) => {
                setSpeedyRewardingFilter(e.target.value as string);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              label="Speedy Rewarding"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Has Tiers</InputLabel>
            <Select
              value={hasTiersFilter}
              onChange={(e) => {
                setHasTiersFilter(e.target.value as string);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              label="Has Tiers"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Has Tiers</MenuItem>
              <MenuItem value="false">No Tiers</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as string);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              label="Sort By"
            >
              <MenuItem value="merchant_name">Name</MenuItem>
              <MenuItem value="rate_numeric">Rate</MenuItem>
              <MenuItem value="category">Category</MenuItem>
              <MenuItem value="added_date">Added Date</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as string);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              label="Order"
            >
              <MenuItem value="ASC">Ascending</MenuItem>
              <MenuItem value="DESC">Descending</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} md={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => {
                  setShowInactive(e.target.checked);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                color="primary"
              />
            }
            label="Show Inactive"
          />
        </Grid>

        <Grid item xs={6} md={2}>
          <Button
            variant="outlined"
            onClick={handleResetFilters}
            startIcon={<FilterListIcon />}
            fullWidth
          >
            Reset Filters
          </Button>
        </Grid>

        <Grid item xs={6} md={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchMerchants()}
            startIcon={<RefreshIcon />}
            fullWidth
          >
            Refresh
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  // Render merchants table
  const renderMerchantsTable = () => {
    if (loading) {
      return (
        <div className={classes.loadingOverlay}>
          <CircularProgress size={60} />
        </div>
      );
    }

    if (merchants.length === 0) {
      return (
        <Typography className={classes.noDataMessage}>
          No merchants found matching your criteria
        </Typography>
      );
    }

    return (
      <>
        <Paper className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                  Logo
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                  Merchant Name
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                  Merchant ID
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                  Category
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                  Rate
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                  Status
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                  Features
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {merchants.map((merchant) => (
                <TableRow key={merchant.id}>
                  <TableCell className={classes.tableCell}>
                    {merchant.logo ? (
                      <img
                        src={merchant.logo}
                        alt={merchant.merchant_name}
                        className={classes.merchantLogo}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {merchant.merchant_name}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {merchant.merchant_id}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {merchant.category || "-"}
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.rateCell}`}>
                    {merchant.rate || (merchant.rate_numeric ? `${merchant.rate_numeric}%` : "-")}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {merchant.is_blacklisted ? (
                      <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                        <Chip
                          label="Blacklisted"
                          size="small"
                          className={classes.chipBlacklisted}
                        />
                        {merchant.blacklist_reason_code && (
                          <Chip
                            label={formatBlacklistReasonCode(merchant.blacklist_reason_code)}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {merchant.blacklist_watch && (
                          <Chip label="Watch" size="small" color="primary" variant="outlined" />
                        )}
                        {merchant.blacklist_reason && (
                          <Tooltip title={merchant.blacklist_reason}>
                            <Typography variant="caption" noWrap style={{ maxWidth: 140 }}>
                              {merchant.blacklist_reason}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    ) : merchant.is_enabled_on_app ? (
                      <Chip
                        label="Enabled"
                        size="small"
                        className={classes.chipEnabled}
                      />
                    ) : (
                      <Chip
                        label="Disabled"
                        size="small"
                        className={classes.chipDisabled}
                      />
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
                      {merchant.special_offer && (
                        <Chip
                          label="Special Offer"
                          size="small"
                          className={classes.chipSpecialOffer}
                        />
                      )}
                      {merchant.speedy_rewarding && (
                        <Chip
                          label="Speedy"
                          size="small"
                          className={classes.chipSpeedyRewarding}
                        />
                      )}
                      {merchant.tiers && merchant.tiers.length > 0 && (
                        <Chip
                          label={`${merchant.tiers.length} Tiers`}
                          size="small"
                          className={classes.chipTiers}
                          onClick={() => openTiersDialog(merchant)}
                          clickable
                        />
                      )}
                      {merchant.description && (
                        <Chip
                          label="Description"
                          size="small"
                          onClick={() => openDescriptionDialog(merchant)}
                          clickable
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="default"
                        onClick={() => openDetailsDialog(merchant)}
                        className={classes.actionButton}
                      >
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Overrides">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(merchant)}
                        disabled={actionLoading === merchant.merchant_id}
                        className={classes.actionButton}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {merchant.is_blacklisted ? (
                      <Tooltip title="Remove from Blacklist">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openUnblacklistDialog(merchant)}
                          disabled={actionLoading === merchant.merchant_id}
                          className={classes.actionButton}
                        >
                          {actionLoading === merchant.merchant_id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CheckCircleIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Add to Blacklist">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => openBlacklistDialog(merchant)}
                          disabled={actionLoading === merchant.merchant_id}
                          className={classes.actionButton}
                        >
                          {actionLoading === merchant.merchant_id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <BlockIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Test Brand">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openTestDialog(merchant)}
                        disabled={actionLoading === merchant.merchant_id}
                        className={classes.actionButton}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onChangePage={handleChangePage}
          rowsPerPage={pagination.pageSize}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </>
    );
  };

  // Render dialogs
  const renderDialogs = () => (
    <>
      {/* Blacklist Dialog */}
      <Dialog
        open={blacklistDialogOpen}
        onClose={() => setBlacklistDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add to Blacklist</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText>
            Add <strong>{selectedMerchant?.merchant_name}</strong> to the blacklist. This will
            prevent the merchant from appearing in the app. A reason is required.
          </DialogContentText>
          <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 16 }}>
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label="Reason"
              placeholder="Why is this brand being blacklisted?"
              value={blacklistForm.reason}
              onChange={(e) =>
                setBlacklistForm({ ...blacklistForm, reason: e.target.value })
              }
              variant="outlined"
            />
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Reason Code</InputLabel>
              <Select
                value={blacklistForm.reason_code}
                onChange={(e) =>
                  setBlacklistForm({
                    ...blacklistForm,
                    reason_code: e.target.value as string,
                  })
                }
                label="Reason Code"
              >
                {BLACKLIST_REASON_CODES.map((code) => (
                  <MenuItem key={code.value} value={code.value}>
                    {code.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={blacklistForm.watch}
                  onChange={(e) =>
                    setBlacklistForm({ ...blacklistForm, watch: e.target.checked })
                  }
                  color="primary"
                />
              }
              label="Watch (allow auto re-enable when URL test passes)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlacklistDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleBlacklist}
            color="secondary"
            variant="contained"
            disabled={actionLoading !== null || !blacklistForm.reason.trim()}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <BlockIcon />}
          >
            Blacklist
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unblacklist Dialog */}
      <Dialog
        open={unblacklistDialogOpen}
        onClose={() => setUnblacklistDialogOpen(false)}
      >
        <DialogTitle>Remove from Blacklist</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText>
            Are you sure you want to remove <strong>{selectedMerchant?.merchant_name}</strong>{" "}
            from the blacklist? This will allow the merchant to appear in the app again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnblacklistDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleUnblacklist}
            color="primary"
            variant="contained"
            disabled={actionLoading !== null}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            Remove from Blacklist
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Brand Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => {
          setTestDialogOpen(false);
          setTestResult(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Test Brand: {selectedMerchant?.merchant_name}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText>
            Generate a tracking URL for testing this brand. The URL will be created for the
            test user account.
          </DialogContentText>

          {testResult && (
            <div className={classes.trackingUrlContainer}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Test User:</strong> {testResult.test_user_email}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Click ID:</strong> {testResult.click_id}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Tracking URL:</strong>
              </Typography>
              <Link href={testResult.tracking_url} target="_blank" rel="noopener">
                {testResult.tracking_url}
              </Link>
              {testResult.tracking_mobile_url && (
                <>
                  <Typography variant="subtitle2" gutterBottom style={{ marginTop: "1em" }}>
                    <strong>Mobile Tracking URL:</strong>
                  </Typography>
                  <Link href={testResult.tracking_mobile_url} target="_blank" rel="noopener">
                    {testResult.tracking_mobile_url}
                  </Link>
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTestDialogOpen(false);
              setTestResult(null);
            }}
            color="default"
          >
            Close
          </Button>
          {!testResult && (
            <Button
              onClick={handleTestBrand}
              color="primary"
              variant="contained"
              disabled={actionLoading !== null}
              startIcon={actionLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            >
              Generate Tracking URL
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Description Dialog */}
      <Dialog
        open={descriptionDialogOpen}
        onClose={() => setDescriptionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
            <DescriptionIcon color="primary" />
            Description: {selectedMerchant?.merchant_name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedMerchant?.description ? (
            <Typography 
              variant="body1" 
              style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: selectedMerchant.description }}
            />
          ) : (
            <Typography variant="body1" color="textSecondary" style={{ fontStyle: 'italic' }}>
              No description available for this merchant.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tiers Dialog */}
      <Dialog
        open={tiersDialogOpen}
        onClose={() => setTiersDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
            <LayersIcon color="primary" />
            Tiers: {selectedMerchant?.merchant_name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedMerchant?.tiers && selectedMerchant.tiers.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Tier Name</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Rate</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Previous Rate</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Valid From</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Valid To</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedMerchant.tiers.map((tier, index) => (
                  <TableRow key={tier.id || index}>
                    <TableCell>{tier.name}</TableCell>
                    <TableCell style={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {tier.rate || (tier.rate_numeric ? `${parseFloat(tier.rate_numeric) * 100}%` : '-')}
                    </TableCell>
                    <TableCell style={{ color: '#888' }}>
                      {tier.rate_was || '-'}
                    </TableCell>
                    <TableCell>
                      {tier.actual_from ? new Date(tier.actual_from).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {tier.actual_to ? new Date(tier.actual_to).toLocaleDateString() : 'Ongoing'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body1" color="textSecondary" style={{ fontStyle: 'italic' }}>
              No tiers available for this merchant.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTiersDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Overrides Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
            <EditIcon color="primary" />
            <Box>
              <Typography variant="h6">Edit Brand Overrides</Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedMerchant?.merchant_name} ({selectedMerchant?.merchant_id})
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '10px' }}>
            <TextField
              fullWidth
              label="Name Override"
              placeholder="Leave empty to use original name"
              value={editForm.merchant_name_override}
              onChange={(e) => setEditForm({ ...editForm, merchant_name_override: e.target.value })}
              variant="outlined"
              helperText={`Original: ${selectedMerchant?.merchant_name}`}
            />
            
            <TextField
              fullWidth
              label="Logo URL Override"
              placeholder="Leave empty to use original logo"
              value={editForm.merchant_logo_override}
              onChange={(e) => setEditForm({ ...editForm, merchant_logo_override: e.target.value })}
              variant="outlined"
              helperText="Enter a valid image URL"
            />

            <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: '10px' }}>
              Reward Paid Override
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Time (days)"
                  type="number"
                  placeholder="e.g., 30"
                  value={editForm.reward_paid_time}
                  onChange={(e) => setEditForm({ ...editForm, reward_paid_time: e.target.value })}
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Qualifier</InputLabel>
                  <Select
                    value={editForm.reward_paid_qualifier}
                    onChange={(e) => setEditForm({ ...editForm, reward_paid_qualifier: e.target.value as string })}
                    label="Qualifier"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {REWARD_PAID_QUALIFIERS.map((q) => (
                      <MenuItem key={q.value} value={q.value}>
                        {q.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {selectedMerchant?.reward_paid_override && (
              <Box style={{ backgroundColor: '#fff3e0', padding: '12px', borderRadius: '8px' }}>
                <Typography variant="body2">
                  <strong>Current Override:</strong>{' '}
                  {selectedMerchant.reward_paid_override.time && `${selectedMerchant.reward_paid_override.time} days`}
                  {selectedMerchant.reward_paid_override.time && selectedMerchant.reward_paid_override.qualifier && ' '}
                  {selectedMerchant.reward_paid_override.qualifier}
                </Typography>
              </Box>
            )}

            {selectedMerchant?.is_blacklisted && (
              <>
                <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: '10px' }}>
                  Blacklist Metadata
                </Typography>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={2}
                  label="Blacklist Reason"
                  value={editForm.blacklist_reason}
                  onChange={(e) =>
                    setEditForm({ ...editForm, blacklist_reason: e.target.value })
                  }
                  variant="outlined"
                />
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Reason Code</InputLabel>
                  <Select
                    value={editForm.blacklist_reason_code}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        blacklist_reason_code: e.target.value as string,
                      })
                    }
                    label="Reason Code"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {BLACKLIST_REASON_CODES.map((code) => (
                      <MenuItem key={code.value} value={code.value}>
                        {code.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.blacklist_watch}
                      onChange={(e) =>
                        setEditForm({ ...editForm, blacklist_watch: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label="Watch (auto re-enable on URL test pass)"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClearOverrides}
            color="secondary"
            disabled={actionLoading !== null}
          >
            Clear All Overrides
          </Button>
          <Box style={{ flex: 1 }} />
          <Button onClick={() => setEditDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateOverrides}
            color="primary"
            variant="contained"
            disabled={actionLoading !== null}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            Save Overrides
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
            {selectedMerchant?.logo && (
              <img 
                src={selectedMerchant.logo} 
                alt={selectedMerchant.merchant_name}
                style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4 }}
              />
            )}
            <Box>
              <Typography variant="h6">{selectedMerchant?.merchant_name}</Typography>
              <Typography variant="caption" color="textSecondary">
                ID: {selectedMerchant?.merchant_id}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Basic Information
              </Typography>
              <Box style={{ backgroundColor: '#f9f9f9', padding: 16, borderRadius: 8 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Category:</strong> {selectedMerchant?.category || 'N/A'}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Rate:</strong>{' '}
                  <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                    {selectedMerchant?.rate || (selectedMerchant?.rate_numeric ? `${selectedMerchant.rate_numeric}%` : 'N/A')}
                  </span>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Status:</strong> {selectedMerchant?.status || 'N/A'}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Enabled on App:</strong>{' '}
                  <span style={{ color: selectedMerchant?.is_enabled_on_app ? '#4caf50' : '#f44336' }}>
                    {selectedMerchant?.is_enabled_on_app ? 'Yes' : 'No'}
                  </span>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Blacklisted:</strong>{' '}
                  <span style={{ color: selectedMerchant?.is_blacklisted ? '#f44336' : '#4caf50' }}>
                    {selectedMerchant?.is_blacklisted ? 'Yes' : 'No'}
                  </span>
                </Typography>
                {selectedMerchant?.is_blacklisted && (
                  <>
                    <Typography variant="body2" gutterBottom>
                      <strong>Reason Code:</strong>{' '}
                      {formatBlacklistReasonCode(selectedMerchant.blacklist_reason_code) || 'N/A'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Watch:</strong>{' '}
                      {selectedMerchant.blacklist_watch ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Reason:</strong>{' '}
                      {selectedMerchant.blacklist_reason || 'N/A'}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>

            {/* Features */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Features
              </Typography>
              <Box style={{ backgroundColor: '#f9f9f9', padding: 16, borderRadius: 8 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Special Offer:</strong>{' '}
                  <span style={{ color: selectedMerchant?.special_offer ? '#ff9800' : '#888' }}>
                    {selectedMerchant?.special_offer ? 'Yes' : 'No'}
                  </span>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Speedy Rewarding:</strong>{' '}
                  <span style={{ color: selectedMerchant?.speedy_rewarding ? '#2196f3' : '#888' }}>
                    {selectedMerchant?.speedy_rewarding ? 'Yes' : 'No'}
                  </span>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Has Tiers:</strong>{' '}
                  <span style={{ color: selectedMerchant?.tiers ? '#9c27b0' : '#888' }}>
                    {selectedMerchant?.tiers && selectedMerchant.tiers.length > 0 
                      ? `Yes (${selectedMerchant.tiers.length} tiers)` 
                      : 'No'}
                  </span>
                </Typography>
                <Typography variant="body2">
                  <strong>Flagged:</strong>{' '}
                  <span style={{ color: selectedMerchant?.flagged ? '#f44336' : '#888' }}>
                    {selectedMerchant?.flagged ? 'Yes' : 'No'}
                  </span>
                </Typography>
              </Box>
            </Grid>

            {/* Purchase Timeline Stats */}
            {selectedMerchant?.purchase_timeline_stats && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Purchase Timeline
                </Typography>
                <Box style={{ backgroundColor: '#f9f9f9', padding: 16, borderRadius: 8 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Tracking Time:</strong>{' '}
                        {selectedMerchant.purchase_timeline_stats.tracked?.time}{' '}
                        {selectedMerchant.purchase_timeline_stats.tracked?.unit === 'd' ? 'days' : selectedMerchant.purchase_timeline_stats.tracked?.unit}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Award Time:</strong>{' '}
                        {selectedMerchant.purchase_timeline_stats.awarded?.time}{' '}
                        {selectedMerchant.purchase_timeline_stats.awarded?.unit === 'd' ? 'days' : selectedMerchant.purchase_timeline_stats.awarded?.unit}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}

            {/* Reward Paid Override */}
            {selectedMerchant?.reward_paid_override && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Reward Paid Override
                </Typography>
                <Box style={{ backgroundColor: '#fff3e0', padding: 16, borderRadius: 8, border: '1px solid #ffb74d' }}>
                  <Grid container spacing={2}>
                    {selectedMerchant.reward_paid_override.time !== undefined && (
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Time:</strong> {selectedMerchant.reward_paid_override.time} days
                        </Typography>
                      </Grid>
                    )}
                    {selectedMerchant.reward_paid_override.qualifier && (
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Qualifier:</strong> {selectedMerchant.reward_paid_override.qualifier}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            )}

            {/* Description */}
            {selectedMerchant?.description && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Description
                </Typography>
                <Box style={{ backgroundColor: '#f9f9f9', padding: 16, borderRadius: 8 }}>
                  <Typography 
                    variant="body2" 
                    style={{ whiteSpace: 'pre-wrap' }}
                    dangerouslySetInnerHTML={{ __html: selectedMerchant.description }}
                  />
                </Box>
              </Grid>
            )}

            {/* Tiers */}
            {selectedMerchant?.tiers && selectedMerchant.tiers.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Tiers ({selectedMerchant.tiers.length})
                </Typography>
                <Table size="small" style={{ backgroundColor: '#f9f9f9' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 'bold' }}>Tier Name</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Rate</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Valid Period</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedMerchant.tiers.map((tier, index) => (
                      <TableRow key={tier.id || index}>
                        <TableCell>{tier.name}</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#1976d2' }}>
                          {tier.rate || '-'}
                        </TableCell>
                        <TableCell>
                          {tier.actual_from || tier.actual_to 
                            ? `${tier.actual_from ? new Date(tier.actual_from).toLocaleDateString() : 'Start'} - ${tier.actual_to ? new Date(tier.actual_to).toLocaleDateString() : 'Ongoing'}`
                            : 'Always'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return (
    <div className={classes.main}>
      <Typography variant="h3" component="h1" className={classes.mainHeading}>
        VDX Merchants
      </Typography>

      {renderMetadataCards()}
      {renderFilters()}
      {renderMerchantsTable()}
      {renderDialogs()}

      <Notification />
    </div>
  );
};

export default VdxMerchantsDashboard;
